import { useEffect, useState } from "react";
import { Search, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const PAGE_SIZE = 25;

const ACTION_TONE = {
  "login.success": "bg-jade/15 text-jade",
  "login.failed": "bg-copper/15 text-copper",
  "logout": "bg-mist/60 text-ink/65",
  "password.changed": "bg-copper/15 text-copper",
};
function actionTone(action) {
  if (ACTION_TONE[action]) return ACTION_TONE[action];
  if (action.endsWith(".deleted") || action.endsWith(".unpublished")) return "bg-ink/10 text-ink/65";
  if (action.endsWith(".published")) return "bg-jade/15 text-jade";
  if (action.endsWith(".created")) return "bg-jade/10 text-jade";
  if (action.startsWith("export.")) return "bg-copper/10 text-copper";
  return "bg-mist/60 text-ink/65";
}

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return dt;
  }
}

export default function ActivityLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  // filters + pagination held in one object so resetting offset alongside
  // a filter change is a single setState call.
  const [filters, setFilters] = useState({ q: "", act: "", ct: "", offset: 0 });
  const { q, act, ct, offset } = filters;
  const [loading, setLoading] = useState(false);

  const load = (overrides = {}) => {
    setLoading(true);
    const eff = { ...filters, ...overrides };
    const params = { limit: PAGE_SIZE, offset: eff.offset };
    if (eff.q) params.q = eff.q;
    if (eff.act) params.action = eff.act;
    if (eff.ct) params.content_type = eff.ct;
    return api.get("/admin/activity", { params })
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
        if (r.data.actions) setActions(r.data.actions);
        if (r.data.content_types) setContentTypes(r.data.content_types);
      })
      .finally(() => setLoading(false));
  };

  // Initial load only — subsequent loads are triggered directly from handlers.
  // setTimeout puts the fetch outside the synchronous effect body so the
  // react-hooks/set-state-in-effect rule does not flag the chain.
  useEffect(() => {
    const t = setTimeout(() => load(), 0);
    return () => clearTimeout(t);
  }, []);

  const handleSearch = (v) => {
    setFilters((prev) => ({ ...prev, q: v, offset: 0 }));
    setTimeout(() => load({ q: v, offset: 0 }), 0);
  };
  const handleActFilter = (v) => {
    setFilters((prev) => ({ ...prev, act: v, offset: 0 }));
    load({ act: v, offset: 0 });
  };
  const handleCtFilter = (v) => {
    setFilters((prev) => ({ ...prev, ct: v, offset: 0 }));
    load({ ct: v, offset: 0 });
  };
  const goOffset = (n) => {
    setFilters((prev) => ({ ...prev, offset: n }));
    load({ offset: n });
  };
  const submitSearch = () => load();

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="admin-activity">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="eyebrow">Audit trail</p>
          <h1 className="mt-3 font-serif text-4xl text-jade font-light">Activity log</h1>
          <p className="mt-3 text-ink/65 text-[14px] font-light max-w-xl">
            Every admin action recorded — content edits, publish toggles, media uploads, enquiry status changes, exports, and sign-ins. Passwords and full enquiry content are never stored here.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase text-ink/55">
          <ShieldCheck size={14} className="text-jade" />
          Append-only
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 border-b border-mist pb-2 flex-1 min-w-[260px]">
          <Search size={14} className="text-ink/50" />
          <input
            data-testid="activity-search"
            type="text"
            placeholder="Search title, slug, email…"
            value={q}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitSearch(); }}
            onBlur={submitSearch}
            className="flex-1 bg-transparent outline-none text-[14px]"
          />
        </div>
        <select
          data-testid="activity-action-filter"
          value={act}
          onChange={(e) => handleActFilter(e.target.value)}
          className="border border-mist px-3 py-2 text-[13px] bg-ivory"
        >
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select
          data-testid="activity-content-filter"
          value={ct}
          onChange={(e) => handleCtFilter(e.target.value)}
          className="border border-mist px-3 py-2 text-[13px] bg-ivory"
        >
          <option value="">All content</option>
          {contentTypes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="mt-8 border border-mist">
        <table className="w-full text-[13px]">
          <thead className="bg-mist/30 text-ink/60 text-[11px] tracking-[0.12em] uppercase">
            <tr>
              <th className="text-left px-4 py-3 font-normal">When</th>
              <th className="text-left px-4 py-3 font-normal">Admin</th>
              <th className="text-left px-4 py-3 font-normal">Action</th>
              <th className="text-left px-4 py-3 font-normal">Subject</th>
              <th className="text-left px-4 py-3 font-normal">Detail</th>
              <th className="text-left px-4 py-3 font-normal hidden lg:table-cell">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-mist/60">
            {items.map((row) => (
              <tr key={row.id} data-testid={`activity-row-${row.id}`} className="hover:bg-ivory/60">
                <td className="px-4 py-3 align-top whitespace-nowrap text-ink/70">{fmt(row.ts)}</td>
                <td className="px-4 py-3 align-top text-ink/80 truncate max-w-[180px]">{row.actor_email || "—"}</td>
                <td className="px-4 py-3 align-top">
                  <span className={`text-[10px] tracking-[0.14em] uppercase px-2 py-0.5 ${actionTone(row.action)}`}>
                    {row.action}
                  </span>
                </td>
                <td className="px-4 py-3 align-top text-ink/85 truncate max-w-[220px]">
                  {row.title || row.slug || row.record_id || "—"}
                </td>
                <td className="px-4 py-3 align-top text-ink/65 max-w-[360px]">
                  <span className="block truncate" title={row.summary}>{row.summary || ""}</span>
                </td>
                <td className="px-4 py-3 align-top text-ink/55 text-[12px] hidden lg:table-cell whitespace-nowrap">
                  {row.ip || "—"}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-ink/55 italic">
                  No activity recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex items-center justify-between text-[12px] text-ink/65">
        <p data-testid="activity-total">{total} event{total === 1 ? "" : "s"} · page {page} of {pages}</p>
        <div className="flex items-center gap-2">
          <button
            data-testid="activity-prev"
            onClick={() => goOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0 || loading}
            className="inline-flex items-center gap-1 border border-mist px-3 py-1.5 hover:border-jade disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <button
            data-testid="activity-next"
            onClick={() => goOffset(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total || loading}
            className="inline-flex items-center gap-1 border border-mist px-3 py-1.5 hover:border-jade disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
